import { FilePurpose } from '@/api/enums';
import {
  ContentMedia,
  EmailAttachment,
  InteractiveElement,
  InteractiveElementType,
} from '@/client/courses';
import { ModifiedQuestion } from '@/client/quizzes/types/quizzes.type';
import {
  AiCustomizationPanel,
  ContentMediaUpload,
  EditorSidebar,
  EditorSidebarPanel,
  LanguageSettingsPanel,
  MediaSettingsPanel,
} from '@/components/content-editor/sidebar';
import { AttachmentTypeMenu } from '@/components/slides/admin/AttachmentTypeMenu';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { AppButton } from '@/ui/buttons';
import { AppIconTooltip } from '@/ui/icon-tooltip';
import { RichTextEditor, RichTextEditorHandle } from '@/ui/rich-text';
import { FlexContainer } from '@/ui/styled-ui';
import { isVideoFile } from '@/utils/helpers/content-media.helper';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ExplanationEditorWrapper = styled.div`
  .rich-text-editor-root {
    min-height: unset;
    height: auto;
    background: var(--beige-main);
    border: 1px solid var(--beige-main);
    border-radius: var(--small-border-radius);

    &:hover {
      border-color: var(--red-main);
    }

    &.p-editor-focused {
      border-color: var(--red-main);
      box-shadow: var(--box-shadow-red-no-blur);
    }

    .ql-container {
      border: none;
      font-size: var(--small-font-size);
      font-family: var(--font-family);
      padding: 0;
    }

    .ql-editor {
      padding: var(--small-padding);
      min-height: 80px;
    }

    // Keeps the placeholder on the caret, which the global rule offsets to
    // suit a wider padding than this editor uses
    .ql-blank::before {
      left: var(--small-padding);
    }
  }
`;

interface QuizEditorSidebarProps {
  selectedLanguage: string;
  translationKey: string;
  availableLanguages: string[];
  onLanguageChange: (lang: string) => void;
  onCopyToLanguage: (targetLanguages: string[]) => void;

  currentQuestion: ModifiedQuestion;
  onMediaChange: (media: ContentMedia | null) => void;
  onInteractiveElementChange: (
    interactiveElement: InteractiveElement | undefined,
  ) => void;

  explanationText: string;
  onExplanationChange: (value: string) => void;
  registerEditor: (handle: RichTextEditorHandle | null) => void;
  unregisterEditor: () => void;

  isAiCustomizationEnabled: boolean;
  aiCustomizationInstructions: string;
  onAiCustomizationToggle: (enabled: boolean) => void;
  onAiInstructionsChange: (value: string) => void;

  onSave: () => void;
  isSaving: boolean;
  isDisabled?: boolean;
  setIsSubmitDisabled: (isSubmitDisabled: boolean) => void;
}

export const QuizEditorSidebar: React.FC<QuizEditorSidebarProps> = ({
  isDisabled,
  setIsSubmitDisabled,
  selectedLanguage,
  translationKey,
  availableLanguages,
  onLanguageChange,
  onCopyToLanguage,
  currentQuestion,
  onMediaChange,
  onInteractiveElementChange,
  explanationText,
  onExplanationChange,
  registerEditor,
  unregisterEditor,
  isAiCustomizationEnabled,
  aiCustomizationInstructions,
  onAiCustomizationToggle,
  onAiInstructionsChange,
  onSave,
  isSaving,
}) => {
  const { t } = useTranslation();
  const account = useAppSelector(selectCurrentAccount);
  const editorRef = useRef<RichTextEditorHandle>(null);

  const [openImageSettings, setOpenImageSettings] = useState(false);

  useEffect(() => {
    setOpenImageSettings(false);
  }, [currentQuestion?.id, currentQuestion?.clientKey, translationKey]);
  const [isUploading, setIsUploading] = useState(false);

  const handleExplanationFocus = () => {
    registerEditor(editorRef.current);
  };

  const handleAttachmentChange = (attachment: EmailAttachment | null) => {
    const element =
      currentQuestion.translations[translationKey]?.interactiveElement;
    if (!element) return;

    onInteractiveElementChange({
      ...element,
      data: { ...(element.data || {}), attachment: attachment || undefined },
    });
  };

  const translationData = currentQuestion.translations[translationKey];
  const media = translationData?.media ?? null;

  return (
    <EditorSidebar>
      <h2>{t('content.quizEditor.title')}</h2>

      <LanguageSettingsPanel
        selectedLanguage={selectedLanguage}
        languages={availableLanguages}
        onLanguageChange={onLanguageChange}
        onCopyToLanguage={onCopyToLanguage}
      />

      <EditorSidebarPanel header={t('content.question.settings')}>
        <ContentMediaUpload
          preselectedFile={media?.file ?? null}
          itemId={currentQuestion?.id ?? 'current-question'}
          purpose={FilePurpose.QUIZZES_MATERIALS}
          preselectedInteractiveElement={
            translationData?.interactiveElement || null
          }
          onInteractiveElementAdd={(_questionId, interactiveElement) =>
            onInteractiveElementChange(interactiveElement)
          }
          onInteractiveElementUpdate={onInteractiveElementChange}
          setIsSubmitDisabled={(uploading) => {
            setIsUploading(uploading);
            setIsSubmitDisabled(uploading);
          }}
          additionalImgOptions={
            <>
              {translationData?.interactiveElement?.type ===
                InteractiveElementType.EMAIL && (
                <AttachmentTypeMenu
                  attachment={
                    translationData?.interactiveElement?.data?.attachment ??
                    null
                  }
                  onChange={handleAttachmentChange}
                  menuId={`quiz-attachment-type-menu-${
                    currentQuestion?.id ?? currentQuestion?.clientKey ?? 'new'
                  }`}
                  triggerClassName="m-auto"
                />
              )}
              {!translationData?.interactiveElement && media?.file && (
                <MediaSettingsPanel
                  media={media}
                  isOpen={openImageSettings}
                  onOpen={() => setOpenImageSettings(true)}
                  onClose={() => setOpenImageSettings(false)}
                  onChange={(settings) =>
                    onMediaChange({ ...media, ...settings })
                  }
                />
              )}
            </>
          }
          onFileUpload={(file) => {
            onMediaChange(
              file ? { ...(media ?? { withBorder: true }), file } : null,
            );
            if (isVideoFile(file)) setOpenImageSettings(true);
          }}
        />
      </EditorSidebarPanel>

      <EditorSidebarPanel
        header={
          <FlexContainer justify="flex-start" align="center" gap={0}>
            {t('content.quizEditor.explanation')}
            <AppIconTooltip
              target="quiz-explanation-tooltip"
              content={t('content.quizEditor.explanation.tooltip')}
            />
          </FlexContainer>
        }
      >
        <ExplanationEditorWrapper>
          <RichTextEditor
            key={`explanation-${
              currentQuestion.id ?? currentQuestion.clientKey ?? 'new'
            }-${translationKey}`}
            ref={editorRef}
            defaultValue={explanationText}
            onChange={onExplanationChange}
            onFocus={handleExplanationFocus}
            onBlur={unregisterEditor}
            placeholder={t('content.quizEditor.explanation.placeholder')}
          />
        </ExplanationEditorWrapper>
      </EditorSidebarPanel>

      {account?.isSystem && (
        <AiCustomizationPanel
          enabled={isAiCustomizationEnabled}
          instructions={aiCustomizationInstructions}
          onToggle={onAiCustomizationToggle}
          onInstructionsChange={onAiInstructionsChange}
          tooltipTarget="quiz-ai-instructions-tooltip"
          tooltipKey="content.question.aiCustomizationInstructions.tooltip"
          disabledKey="content.question.aiCustomizationInstructions.disabled"
        />
      )}

      <AppButton
        label={t('content.quizEditor.saveQuiz')}
        style={{ marginTop: 'auto' }}
        isDisabled={isDisabled || isSaving || isUploading}
        onClick={onSave}
      />
    </EditorSidebar>
  );
};
